import { Grid, Tooltip } from '@mui/material';
import { Action } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import { CSSProperties, useState } from 'react';
import { CommentPopperButton, DiscountPopperButton } from '..';
import { getPriceChangeColors, toPriceString } from '../../../utils';
import { PriceChangeDirection } from '../../PricePrgClient';
import { PriceCellLoadingIndicator } from './PriceCellLoadingIndicator';

type Props = {
    priceId?: number;
    basePrice?: number;
    price?: number;
    priceChangeDirection?: PriceChangeDirection;
    updatedBy?: string;
    updatedDate?: Date;
    defaultBgColor?: string;
    defaultColor?: string;
    isSaving?: boolean;
    commentOpts?: {
        commentable: boolean;
        comment: string;
        canWrite: boolean;
        saveCommentActionCreator(priceId: number, comment: string | null): Action;
    };
    priceStringSuffix?: string;
    discountOpts?: {
        discountable: boolean;
        discount: number;
        canWrite: boolean;
        title?: string;
        saveDiscountActionCreator(priceId: number, discount: number | null): Action;
    };
    styles?: CSSProperties;
    showTooltips: boolean;
    exchangeRate?: number;
    pricelistCurrency?: string;
    defaultCurrency?: string;
};

function getPriceStr(price: number | undefined, discount?: number, priceStringSuffix?: string) {
    if (price == null || isNaN(price) || !isFinite(price)) {
        return '\u00A0';
    }

    let priceStr = toPriceString(price);
    if ((priceStringSuffix ?? '') !== '') {
        priceStr += ' ' + priceStringSuffix;
    }
    if (discount == null || discount <= 0) {
        return priceStr;
    }

    return `${priceStr} (-${toPriceString(discount)})`;
}

export const PriceCell = ({
    priceId,
    basePrice,
    price,
    priceChangeDirection,
    updatedBy,
    updatedDate,
    defaultBgColor,
    defaultColor,
    commentOpts,
    discountOpts,
    isSaving,
    priceStringSuffix,
    styles,
    showTooltips,
    defaultCurrency,
    exchangeRate,
    pricelistCurrency
}: Props) => {
    const [isCommentOpen, setCommentOpen] = useState(false);
    const [isDiscountOpen, setDiscountOpen] = useState(false);

    const handleSetCommentOpen = (open: boolean) => {
        setCommentOpen(open);
    };

    const handleSetDiscountOpen = (open: boolean) => {
        setDiscountOpen(open);
    };

    const priceCell = (
        <div
            style={{
                ...getPriceChangeColors(priceChangeDirection, defaultBgColor, defaultColor),
                fontWeight: 500,
                margin: '0 -8px',
                paddingLeft: '8px',
                ...styles
            }}
        >
            <Grid container direction="column" spacing={0}>
                <Grid item style={{ marginBottom: isSaving ? -3 : 0 }}>
                    {priceId != null && (commentOpts?.commentable || discountOpts?.discountable) ? (
                        <Grid container direction="row" spacing={0} justifyContent="space-between">
                            <Grid item>{getPriceStr(price, discountOpts?.discount, priceStringSuffix)}</Grid>
                            <Grid item>
                                <Grid container direction="row" spacing={0}>
                                    {discountOpts?.discountable && (
                                        <Grid item>
                                            <DiscountPopperButton
                                                title={discountOpts.title}
                                                initialDiscount={discountOpts.discount}
                                                saveDiscountActionCreator={discount =>
                                                    discountOpts.saveDiscountActionCreator(priceId, discount)
                                                }
                                                open={isDiscountOpen}
                                                setOpen={handleSetDiscountOpen}
                                                canWrite={discountOpts.canWrite}
                                                maxDiscount={price ?? 0}
                                            />
                                        </Grid>
                                    )}
                                    {commentOpts?.commentable && (
                                        <Grid item>
                                            <CommentPopperButton
                                                initialComment={commentOpts.comment}
                                                saveCommentActionCreator={comment =>
                                                    commentOpts.saveCommentActionCreator(priceId, comment)
                                                }
                                                open={isCommentOpen}
                                                setOpen={handleSetCommentOpen}
                                                canWrite={commentOpts.canWrite}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    ) : (
                        getPriceStr(price, discountOpts?.discount, priceStringSuffix)
                    )}
                </Grid>
                <Grid item>{isSaving && <PriceCellLoadingIndicator />}</Grid>
            </Grid>
        </div>
    );

    if (updatedBy == null || updatedDate == null) {
        return priceCell;
    }

    return (
        <Tooltip
            title={
                !isCommentOpen &&
                showTooltips && (
                    <div>
                        <BasePrice
                            basePrice={basePrice}
                            defaultCurrency={defaultCurrency}
                            exchangeRate={exchangeRate}
                            pricelistCurrency={pricelistCurrency}
                        />
                        <div>
                            {`${updatedBy} - ${
                                updatedDate != null ? format(updatedDate, 'dd/MM HH:mm') : ''
                            }`}
                        </div>
                    </div>
                )
            }
            enterDelay={2000}
        >
            {priceCell}
        </Tooltip>
    );
};

type BasePriceProps = Pick<Props, 'basePrice' | 'exchangeRate' | 'pricelistCurrency' | 'defaultCurrency'>;
const BasePrice = ({ basePrice, exchangeRate, defaultCurrency, pricelistCurrency }: BasePriceProps) => {
    if (!basePrice) {
        return null;
    }

    if (exchangeRate === 1 || exchangeRate == null) {
        return <div>Base price: {toPriceString(basePrice)}</div>;
    }

    return (
        <div>
            Base price: {toPriceString(basePrice)} {defaultCurrency ?? ''} (
            {toPriceString(basePrice * exchangeRate)} {pricelistCurrency ?? ''})
        </div>
    );
};
